import { useCallback, useMemo, useRef } from "react";
import classNames from "clsx";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { SwitchButtons } from "../SwitchButtons";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useSnapshot } from "valtio";
import { updateActiveRecipientId } from "src/js/stores/letter/actions";
import { DropdownMenu, DropdownMenuSize, DropdownMenuTriggerAlignment } from "react-migration/components/DropdownMenu";
import { Tooltip } from "react-migration/components/Tooltip";
import { Theme } from "react-migration/lib/theme/Theme";
import { Recipient } from "react-migration/domains/sites/typings/Recipient";
import { letterStore } from "src/js/stores/letter/store";

export enum DropdownTitleLayout {
  HORIZONTAL = "horizontal",
  VERTICAL = "vertical",
}

type RecipientsDropdownProps = {
  titleLayout?: DropdownTitleLayout;
};

export const RecipientsDropdown = ({ titleLayout = DropdownTitleLayout.VERTICAL }: RecipientsDropdownProps) => {
  const { t } = useTranslation();
  const usAccess = hasFeature(Feature.usAccess);

  const { recipients, selectedRecipientIds, activeRecipientId } = useSnapshot(letterStore);

  const isHorizontal = titleLayout === DropdownTitleLayout.HORIZONTAL;

  const triggerRef = useRef<HTMLDivElement>(null);
  const contentWidth = triggerRef?.current?.clientWidth ?? (isHorizontal ? 580 : 320);

  const selectedRecipient = useMemo(() => {
    return recipients?.find((recipient: Recipient) => recipient._id === activeRecipientId);
  }, [activeRecipientId, recipients]);

  const selectedRecipients = recipients?.filter((recipient) => selectedRecipientIds?.includes(recipient._id));

  const currentRecipientIndex = useMemo(() => {
    return activeRecipientId && selectedRecipientIds ? selectedRecipientIds.indexOf(activeRecipientId) : -1;
  }, [activeRecipientId, selectedRecipientIds]);

  const onGetPreviousRecipient = useCallback(() => {
    if (currentRecipientIndex > 0 && selectedRecipientIds) {
      updateActiveRecipientId(selectedRecipientIds[currentRecipientIndex - 1]);
    }
  }, [currentRecipientIndex, selectedRecipientIds]);

  const onGetNextRecipient = useCallback(() => {
    if (selectedRecipientIds && currentRecipientIndex !== selectedRecipientIds.length - 1) {
      updateActiveRecipientId(selectedRecipientIds[currentRecipientIndex + 1]);
    }
  }, [currentRecipientIndex, selectedRecipientIds]);

  const selectedRecipientValue = useMemo(
    () => (
      <Tooltip.Wrapper>
        <Tooltip.Trigger>
          <div className="overflow-hidden pointer-events-auto">
            <div
              className={classNames("flex", "flex-row", "items-start", "text-left", "flex-nowrap", "mr-2", "gap-x-2")}
              data-testid="selected-recipient-placeholder"
            >
              <strong
                className={classNames(
                  isHorizontal ? "max-w-[350px]" : "w-[190px]",
                  "overflow-hidden",
                  "text-ellipsis",
                  "whitespace-nowrap"
                )}
              >
                {selectedRecipient?.fullName}
              </strong>
              {!usAccess && selectedRecipient?.title}
            </div>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content align="center" side="bottom" theme={Theme.Dark} size="sm">
          <span className="whitespace-normal" data-testid="selected-recipient-tooltip-content">
            {selectedRecipient?.fullName}
          </span>
        </Tooltip.Content>
      </Tooltip.Wrapper>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRecipient]
  );

  return (
    <div
      className={classNames(
        "flex",
        "flex-row",
        "w-full",
        "border-b",
        "border-border-action",
        "justify-center",
        isHorizontal ? "py-2" : "pt-11 pb-3 mb-5"
      )}
      data-testid="recipient-select-dropdown"
    >
      <div
        className={classNames(
          "flex justify-between",
          isHorizontal ? [usAccess ? "w-letter-width" : "w-a4-width", "mr-3", "gap-2"] : "w-full"
        )}
      >
        <div
          className={classNames(
            "items-center",
            isHorizontal ? "flex flex-row w-full gap-2" : "flex-auto flex-wrap mr-3 -mt-8"
          )}
        >
          <div
            className={classNames(
              "font-semibold",
              "text-sm",
              "leading-5",
              "text-text-base-secondary",
              !isHorizontal && "mb-3"
            )}
          >
            {t(isHorizontal ? "sites.letters.recipient" : "sites.card.letters.select_recipient")}
          </div>

          <DropdownMenu.Provider size={DropdownMenuSize.AUTO} className="w-full">
            <DropdownMenu.Trigger
              align={DropdownMenuTriggerAlignment.BOTTOM}
              className="w-full"
              data-testid="recipients-dropdown-trigger"
              ref={triggerRef}
            >
              <div
                className={classNames(
                  "flex",
                  "flex-1",
                  "justify-between",
                  "items-center",
                  "pl-3",
                  "pr-2",
                  "h-10",
                  "bg-white",
                  "border",
                  "border-border-action",
                  "rounded-sm"
                )}
              >
                <span>{selectedRecipientValue}</span>
                <i className="text-xl icon-lt-arrow-down-s-line" />
              </div>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              style={{ width: contentWidth }}
              className={classNames(
                "border",
                "border-border-action",
                "rounded-sm",
                "max-h-[500px]!",
                "overflow-y-auto"
              )}
            >
              {selectedRecipients?.map((recipient) => (
                <DropdownMenu.Item
                  key={recipient._id}
                  style={{ maxWidth: contentWidth }}
                  className={classNames(
                    {
                      "bg-neutral-100": recipient._id === selectedRecipient?._id,
                    },
                    "flex",
                    "flex-col",
                    "py-2",
                    "px-3"
                  )}
                  onClick={() => updateActiveRecipientId(recipient._id)}
                >
                  <strong>{recipient.fullName}</strong>
                  {!usAccess && <span>{recipient.title}</span>}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Provider>
        </div>

        <SwitchButtons
          currentIndex={currentRecipientIndex}
          totalCount={selectedRecipientIds?.length ?? 0}
          onPreviousBtnClick={onGetPreviousRecipient}
          onNextBtnClick={onGetNextRecipient}
        />
      </div>
    </div>
  );
};
